import { Box } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  InsightsHeaderSelectorField,
  InsightsHeaderSelectorRow,
} from "@/components/common/ReportingPageHeaderShell";
import { DataFreshnessFooter } from "@/components/common/DataFreshnessFooter";
import { LoadingRing } from "@/components/common/LoadingRing";
import { PageShell } from "@/components/common/PageShell";
import ReportingWindowSelector, {
  type DatePeriod,
  type TrendPeriod,
} from "@/components/common/ReportingWindowSelector";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useDateRangeBadge } from "@/hooks/useDateRangeBadge";
import { type Customer as StoreCustomer, type Store } from "@/services/features/StoreService";
import { type DateRange } from "@/services/StorePerformanceService";
import { formatStoreSelectorLabel } from "@/utils/store";

type ReportPeriod = DatePeriod;

interface StoreInsightsReportHeaderProps {
  children?: React.ReactNode;
  title: string;
  icon: React.ReactNode;
  isCraveUser: boolean;
  currentUserRole?: string;
  customers: StoreCustomer[];
  selectedCustomerIds: number[];
  onCustomerChange: (nextIds: number[]) => void;
  groups: string[];
  selectedGroups: string[] | null;
  groupCounts: Record<string, number>;
  onGroupsChange: (nextGroups: string[]) => void;
  filteredStores: Store[];
  selectedStores: Store[];
  onStoresChange: (nextStores: Store[]) => void;
  loading: boolean;
  networkLoading?: boolean;
  period: ReportPeriod;
  availablePeriods?: readonly ReportPeriod[];
  currentDateRange: DateRange;
  customRange?: DateRange;
  onPeriodChange: (nextPeriod: ReportPeriod) => void;
  onCustomRangeChange: (nextRange: DateRange) => void;
  deviceTypeFilterEnabled?: boolean;
  deviceTypeFilterDisabled?: boolean;
  selectedDeviceType?: DeviceType;
  onDeviceTypeChange?: (nextDeviceType: DeviceType) => void;
  includeAllDeviceOption?: boolean;
  freshnessText?: string;
  notice?: React.ReactNode;
  showTrendSelector?: boolean;
  trendPeriod?: TrendPeriod;
  onTrendPeriodChange?: (trendPeriod: TrendPeriod) => void;
  trendComparisonRange?: DateRange;
  anchorTrendComparisonToWeekdays?: boolean;
  onAnchorTrendComparisonToWeekdaysChange?: (anchored: boolean) => void;
}

export const StoreInsightsReportHeader: React.FC<StoreInsightsReportHeaderProps> = ({
  children,
  title,
  icon,
  isCraveUser,
  currentUserRole,
  customers,
  selectedCustomerIds,
  onCustomerChange,
  groups,
  selectedGroups,
  groupCounts,
  onGroupsChange,
  filteredStores,
  selectedStores,
  onStoresChange,
  loading,
  networkLoading = false,
  period,
  availablePeriods,
  currentDateRange,
  customRange,
  onPeriodChange,
  onCustomRangeChange,
  deviceTypeFilterEnabled = false,
  deviceTypeFilterDisabled = false,
  selectedDeviceType = "all",
  onDeviceTypeChange,
  includeAllDeviceOption = true,
  freshnessText,
  notice,
  showTrendSelector = false,
  trendPeriod,
  onTrendPeriodChange,
  trendComparisonRange,
  anchorTrendComparisonToWeekdays,
  onAnchorTrendComparisonToWeekdaysChange,
}) => {
  const { t } = useTranslation();
  const { freshnessFooterIcon, freshnessFooterText, freshnessFooterTooltip } = useDateRangeBadge({
    period,
    freshnessText,
  });

  return (
    <PageShell
      title={title}
      icon={icon}
      titleAccessory={<LoadingRing loading={networkLoading} />}
      headerActions={
        <InsightsHeaderSelectorRow>
          {isCraveUser && (
            <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
              <StoreMultiSelect
                className="w-full"
                minDropdownWidth={300}
                dropdownAlign="right"
                options={customers}
                value={customers.filter((c) => selectedCustomerIds.includes(c.id))}
                currentUserRole={currentUserRole}
                onChange={(next) => onCustomerChange(next.map((c) => c.id))}
                label={t("storeInsights.common.fields.customers")}
                loading={loading}
                getOptionLabel={(c) => c.name}
                getOptionKey={(c) => c.id}
                emptyText={t("storeInsights.common.empty.noCustomers")}
              />
            </InsightsHeaderSelectorField>
          )}

          <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 160, lg: 300 } }}>
            <StoreMultiSelect
              className="w-full"
              minDropdownWidth={300}
              dropdownAlign="right"
              rowHeight="default"
              options={groups}
              value={selectedGroups ?? []}
              currentUserRole={currentUserRole}
              isDemo={(g) => g.toLowerCase().includes("demo")}
              onChange={onGroupsChange}
              label={t("storeInsights.common.fields.groups")}
              loading={loading}
              getOptionLabel={(g) => (g === "" ? t("storeInsights.common.options.ungrouped") : g)}
              getOptionKey={(g) => g}
              getOptionSecondary={(g) => {
                const n = groupCounts[g] ?? 0;
                return t("storeInsights.common.storeCount", { count: n });
              }}
            />
          </InsightsHeaderSelectorField>

          <InsightsHeaderSelectorField sx={{ width: { xs: "100%", md: 180, lg: 350 } }}>
            <StoreMultiSelect
              className="w-full font-bold"
              minDropdownWidth={440}
              dropdownAlign="right"
              options={filteredStores}
              value={selectedStores}
              currentUserRole={currentUserRole}
              isDemo={(s) => {
                const name = String((s as any).name || "").toLowerCase();
                const group = String((s as any).group || "").toLowerCase();
                return name.includes("demo") || group.includes("demo");
              }}
              onChange={onStoresChange}
              label={t("storeInsights.common.fields.stores")}
              disabled={loading}
              loading={loading}
              getOptionLabel={(s) => formatStoreSelectorLabel(s, t, currentUserRole, "id-name")}
              getOptionKey={(s) => s.id}
            />
          </InsightsHeaderSelectorField>
        </InsightsHeaderSelectorRow>
      }
      contextControls={
        <div className="w-full mb-6">
          <ReportingWindowSelector
            value={period}
            onChange={onPeriodChange}
            availablePeriods={availablePeriods}
            showRangeText
            dateRange={currentDateRange}
            customRange={customRange}
            onCustomRangeChange={onCustomRangeChange}
            showDeviceFilter={deviceTypeFilterEnabled}
            deviceFilterDisabled={deviceTypeFilterDisabled}
            selectedDeviceType={selectedDeviceType}
            onDeviceTypeChange={onDeviceTypeChange}
            includeAllDeviceOption={includeAllDeviceOption}
            showTrendSelector={showTrendSelector}
            trendPeriod={trendPeriod}
            onTrendPeriodChange={onTrendPeriodChange}
            trendComparisonRange={trendComparisonRange}
            anchorTrendComparisonToWeekdays={anchorTrendComparisonToWeekdays}
            onAnchorTrendComparisonToWeekdaysChange={onAnchorTrendComparisonToWeekdaysChange}
          />
          {notice ? <Box sx={{ mt: 2 }}>{notice}</Box> : null}
        </div>
      }
      footer={
        <DataFreshnessFooter
          icon={freshnessFooterIcon}
          text={freshnessFooterText}
          tooltip={freshnessFooterTooltip}
        />
      }
      bodySx={children ? { pt: 0, pb: 4 } : { display: "none" }}
    >
      {children}
    </PageShell>
  );
};
